import CancellableHelper from "@/requests/CancellableHelper";
import GenericTableRequest from "@/models/api/GenericTableRequest";
import { DocumentsListResponse } from "@/models/api/documents/DocumentsList";
import { DocumentServiceItem, DocumentServicesListResponse } from "@/models/api/documents/DocumentServicesList";
import { DocumentPaymentListItem, DocumentPaymentsList } from "@/models/api/documents/DocumentPaymentsList";
import { AdvanceDocumentsList } from "@/models/api/documents/AdvanceDocumentsList";
import { GenerateDocumentRequest } from "@/models/api/documents/GenerateDocumentRequest";
import { axiosInstance } from "@/plugins/axios";
import { SelectItem } from "@/models/api/SelectItem";
import { CorrectionNote } from "@/models/api/documents/CorrectionNote";
import { AddCorrectionNoteResponse } from "@/models/api/documents/AddCorrectionNoteResponse";
import { VatGroupSum } from "@/models/api/documents/VatGroupSum";
import { DocumentToPrint } from "@/models/api/mail/DocumentToPrint";
import { DocumentToPrintResponse } from "@/models/api/documents/DocumentToPrintResponse";
import { FileGuid } from "@/models/api/Files";
import { ReprintDocumentRequest } from "@/models/api/documents/ReprintDocumentRequest";
import { CreateDocumentResponse } from "@/models/api/documents/CreateDocumentResponse";
import { Contractor } from "@/models/api/Contractor";
import { AxiosResponse } from "axios";
import { DoubleConfirm } from "@/models/api/DoubleConfirm";
import { DetailsDocumentToCancel } from "@/models/api/documents/DetailsDocumentToCancel";

//#region Routes
export const URL_GET_DOCUMENTS_PAGED = "/Document/Paged";
export const URL_GET_DOCUMENTS_TO_PRINT_PAGED = "/Document/DocumentsToPrint/Paged";
export const URL_POST_PRINT_DOCUMENTS_TO_PRINT = "/Document/PrintSelectedDocuments";
// export const URL_GET_SELECTED_DOCUMENTS_TO_PRINT_PAGED = "/Document/SelectedDocumentsToPrint/Paged";
export const URL_GET_ADVANCE_DOCUMENTS_PAGED = "/Document/AdvanceDocuments/Paged";
export const URL_GET_DOCUMENT_PAYMENTS_TABLE = "/Document/${id}/Payments";
export const URL_GET_DOCUMENT_SERVICES_TABLE = "/Document/${id}/DocumentItems";
export const URL_POST_DUPLICATE_DOCUMENT = "/Document/GenerateDuplicate";
export const URL_POST_CANCEL_DOCUMENT = "/Document/${DocumentId}/Cancel";
export const URL_GET_DETAILS_DOCUMENT_TO_CANCEL = "/Document/${DocumentId}/DetailsToCancel";
export const URL_POST_CREATE_DOCUMENT = "/Document/${OrderId}/CreateDocument";
export const URL_GET_SHOW_DOCUMENT = "/Document/${DocumentId}/ShowDocument";
export const URL_GET_SHOW_DOCUMENT_PREVIEW = "/Document/${OrderId}/ShowDocumentPreview";
export const URL_GET_SHOW_KSEF_VISUALIZATION = "/Document/${DocumentId}/ShowKsefVisualization";
export const URL_GET_TRY_SHOW_OFFLINE_VISUALIZATION = "/Document/${DocumentId}/TryShowOfflineVisualization";
export const URL_GET_DOCUMENT_TYPES = "/Document/GetTypes";
export const URL_POST_ADD_CORRECTION_NOTE = "/Document/CorrectionNote/Add";
export const URL_POST_EDIT_CORRECTION_NOTE = "/Document/CorrectionNote/${DocumentId}/Edit";
export const URL_GET_CORRECTION_NOTE = "/Document/CorrectionNote/${DocumentId}";
export const URL_GET_VAT_GROUP_SUMS = "/Document/${DocumentId}/VatGroupSums";
export const URL_POST_VAT_GROUP_SUMS = "/Document/VatGroupSums";
export const URL_GET_DOCUMENT_PAIR_FILES = "/Document/${DocumentId}/PairMailFiles";
export const URL_POST_REPRINT_DOCUMENT = "/Document/Reprint";
export const URL_POST_CREATE_PREVIEW_DOCUMENT = "/Document/CreatePreviewDocument";
export const URL_POST_GENERATE_XML_FILE_FOR_DOCUMENT = "/Document/${DocumentId}/GenerateXml";
export const URL_GET_DOCUMENT_CONTRACTOR = "/Document/${DocumentId}/GetDocumentContractor";
export const URL_POST_GENERATE_VIRTUAL = "/Document/GenerateVirtual";
export const URL_POST_SEND_TO_KSEF = "/Document/${DocumentId}/SendToKSEF";
export const URL_GET_IS_AVAILABLE_KSEF_VISUALIZATION = "/Document/${DocumentId}/IsAvailableVisualization";
export const URL_GET_IS_AVAILABLE_KSEF_OFFLINE_VISUALIZATION =
  "/Document/${DocumentId}/IsAvailableOfflineVisualization";
export const URL_POST_TRY_SET_KSEF_OFFLINE = "/Document/${DocumentId}/TrySetKsefOffline";
export const URL_POST_TOGGLE_KSEF_STOP = "/Document/${DocumentId}/ToggleKsefStop";

//#endregion

const _getDocumentsTable = new CancellableHelper<DocumentsListResponse>(URL_GET_DOCUMENTS_PAGED, DocumentsListResponse);
_getDocumentsTable.jsonSerializer = true;
export const getDocumentsTable = async (genericTableRequest: GenericTableRequest): Promise<DocumentsListResponse> => {
  return await _getDocumentsTable.get(genericTableRequest);
};

const _getDocumentServicesTable = new CancellableHelper<DocumentServicesListResponse>(
  URL_GET_DOCUMENT_SERVICES_TABLE,
  DocumentServiceItem
);
export const getDocumentServicesTable = async (documentId: string): Promise<DocumentServicesListResponse> => {
  return await _getDocumentServicesTable.get({ id: documentId });
};

const _getDocumentPaymentsTable = new CancellableHelper<DocumentPaymentsList>(
  URL_GET_DOCUMENT_PAYMENTS_TABLE,
  DocumentPaymentListItem
);
export const getDocumentPaymentsTable = async (documentId: string): Promise<DocumentPaymentsList> => {
  return await _getDocumentPaymentsTable.get({ id: documentId });
};

const _postDuplicateDocument = new CancellableHelper<DocumentPaymentsList>(URL_POST_DUPLICATE_DOCUMENT, null, true);
export const postDuplicateDocument = async (
  documentId: string,
  duplicateDate: string
): Promise<DocumentPaymentsList> => {
  return await _postDuplicateDocument.post({
    DocumentId: documentId,
    DuplicateDate: duplicateDate,
  });
};

const _postCancelDocument = new CancellableHelper<DoubleConfirm>(URL_POST_CANCEL_DOCUMENT, undefined, true);
export const postCancelDocument = async (
  documentId: string,
  returnBookingToList: boolean,
  confirmed: boolean
): Promise<DoubleConfirm> => {
  return await _postCancelDocument.post({
    DocumentId: documentId,
    ReturnBookingToList: returnBookingToList,
    Confirmed: confirmed,
  });
};

const _getDetailsDocumentToCancel = new CancellableHelper<DetailsDocumentToCancel>(
  URL_GET_DETAILS_DOCUMENT_TO_CANCEL,
  DetailsDocumentToCancel,
  false
);
export const getDetailsDocumentToCancel = async (documentId: string): Promise<DetailsDocumentToCancel> => {
  return _getDetailsDocumentToCancel.get({ DocumentId: documentId });
};

const _getAdvanceDocumentsTable = new CancellableHelper<AdvanceDocumentsList>(
  URL_GET_ADVANCE_DOCUMENTS_PAGED,
  AdvanceDocumentsList
);
_getAdvanceDocumentsTable.jsonSerializer = true;
export const getAdvanceDocumentsTable = async (
  genericTableRequest: GenericTableRequest
): Promise<AdvanceDocumentsList> => {
  return await _getAdvanceDocumentsTable.get(genericTableRequest);
};

const _createDocument = new CancellableHelper<CreateDocumentResponse[]>(
  URL_POST_CREATE_DOCUMENT,
  CreateDocumentResponse,
  false
);
export const createDocument = async (
  orderId: string,
  generateDocumentRequest: GenerateDocumentRequest
): Promise<CreateDocumentResponse[]> => {
  return await _createDocument.post({ OrderId: orderId }, generateDocumentRequest);
};

//TODO - hkopczacki refactor file opening
export const showDocument = async (
  documentId: string,
  // eslint-disable-next-line
  filename: string | undefined = undefined,
  originalCount: number | undefined = undefined,
  copyCount: number | undefined = undefined
): Promise<void> => {
  const response = await axiosInstance.get(URL_GET_SHOW_DOCUMENT.replace("${DocumentId}", documentId), {
    responseType: "blob",
    params: {
      originalCount: originalCount ?? 1,
      copyCount: copyCount ?? 0,
    },
  });

  showFile(response);
};

//TODO - hkopczacki refactor file opening
export const showDocumentPreview = async (orderId: string, selectedLayout: string): Promise<void> => {
  const response = await axiosInstance.get(URL_GET_SHOW_DOCUMENT_PREVIEW.replace("${OrderId}", orderId), {
    responseType: "blob",
    params: { selectedLayout: selectedLayout },
  });

  showFile(response);
};

export const showKsefVisualization = async (documentId: string): Promise<void> => {
  const response = await axiosInstance.get(URL_GET_SHOW_KSEF_VISUALIZATION.replace("${DocumentId}", documentId), {
    responseType: "blob",
  });

  showFile(response);
};

export const tryShowKsefOfflineVisualization = async (documentId: string): Promise<void> => {
  const response = await axiosInstance.get(
    URL_GET_TRY_SHOW_OFFLINE_VISUALIZATION.replace("${DocumentId}", documentId),
    {
      responseType: "blob",
    }
  );

  showFile(response);
};

// eslint-disable-next-line
const showFile = (response: AxiosResponse<any>) => {
  if (response.status != 200) return;

  const disposition = response.headers["content-disposition"];
  const contentType = response.headers["content-type"];

  let filename = "document";
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(disposition);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, "");
  }

  const blob = new Blob([response.data], {
    type: contentType,
  });

  const file = new File([blob], filename, {
    type: contentType,
  });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(file);
  link.target = "_blank";
  link.click();
};

const _createPreviewDocument = new CancellableHelper<number>(URL_POST_CREATE_PREVIEW_DOCUMENT, "number");
export const createPreviewDocument = async (documentsToPrint: DocumentToPrint[]): Promise<number> => {
  return await _createPreviewDocument.post(documentsToPrint);
};

const _getDocumentTypes = new CancellableHelper<Array<SelectItem>>(URL_GET_DOCUMENT_TYPES, SelectItem);
export const getDocumentTypes = async (): Promise<Array<SelectItem>> => {
  return await _getDocumentTypes.get();
};

const _addCorrectionNote = new CancellableHelper<AddCorrectionNoteResponse>(
  URL_POST_ADD_CORRECTION_NOTE,
  AddCorrectionNoteResponse
);
export const addCorrectionNote = async (
  addCorrectionNoteRequest: CorrectionNote
): Promise<AddCorrectionNoteResponse> => {
  return await _addCorrectionNote.post({}, addCorrectionNoteRequest);
};

const _editCorrectionNote = new CancellableHelper<null>(URL_POST_EDIT_CORRECTION_NOTE, null, true);
export const editCorrectionNote = async (
  addCorrectionNoteRequest: CorrectionNote,
  documentId: string
): Promise<null> => {
  return await _editCorrectionNote.post({ DocumentId: documentId }, addCorrectionNoteRequest);
};

const _getCorrectionNote = new CancellableHelper<CorrectionNote>(URL_GET_CORRECTION_NOTE, CorrectionNote);
export const getCorrectionNote = async (documentId: string): Promise<CorrectionNote> => {
  return await _getCorrectionNote.get({ DocumentId: documentId });
};

const _getVatGroupSums = new CancellableHelper<Array<VatGroupSum>>(URL_GET_VAT_GROUP_SUMS, VatGroupSum);
export const getVatGroupSums = async (documentId: string): Promise<Array<VatGroupSum>> => {
  return await _getVatGroupSums.get({ DocumentId: documentId });
};

const _setVatGroupSums = new CancellableHelper<null>(URL_POST_VAT_GROUP_SUMS, null, true);
export const setVatGroupSums = async (documentId: string, vatGroups: Array<VatGroupSum>): Promise<null> => {
  return await _setVatGroupSums.post({}, { DocumentId: documentId, VatGroupSum: vatGroups });
};

const _getDocumentsToPrintPaged = new CancellableHelper<DocumentToPrintResponse>(
  URL_GET_DOCUMENTS_TO_PRINT_PAGED,
  DocumentToPrintResponse
);
_getDocumentsToPrintPaged.jsonSerializer = true;
export const getDocumentsToPrint = async (
  genericTableRequest: GenericTableRequest
): Promise<DocumentToPrintResponse> => {
  return await _getDocumentsToPrintPaged.get(genericTableRequest);
};

// const _getSelectedDocumentsToPrintPaged = new CancellableHelper<DocumentsListResponse>(
//   URL_GET_SELECTED_DOCUMENTS_TO_PRINT_PAGED,
//   DocumentsListResponse
// );
// _getSelectedDocumentsToPrintPaged.jsonSerializer = true;
// export const getSelectedDocumentsToPrint = async (
//   genericTableRequest: GenericTableRequest,
//   documentsIds: Array<string>
// ): Promise<DocumentsListResponse> => {
//   return await _getSelectedDocumentsToPrintPaged.get({ ...genericTableRequest, documentsIds: documentsIds });
// };

const _printSelectedDocuments = new CancellableHelper<number>(URL_POST_PRINT_DOCUMENTS_TO_PRINT, "number");
export const printSelectedDocuments = async (documentsToPrint: Array<DocumentToPrint>): Promise<number> => {
  return await _printSelectedDocuments.post({}, documentsToPrint);
};

const _getDocumentPairFiles = new CancellableHelper<Array<FileGuid>>(URL_GET_DOCUMENT_PAIR_FILES, FileGuid);
export const getDocumentPairFiles = async (documentId: string): Promise<Array<FileGuid>> => {
  return await _getDocumentPairFiles.get({ DocumentId: documentId });
};

const _reprintDocument = new CancellableHelper<DoubleConfirm>(URL_POST_REPRINT_DOCUMENT, null, true);
export const reprintDocument = async (
  reprintDocumentRequest: ReprintDocumentRequest,
  confirmed: boolean
): Promise<DoubleConfirm> => {
  return await _reprintDocument.post({ Confirmed: confirmed }, reprintDocumentRequest);
};

const _generateXmlDocument = new CancellableHelper<number>(URL_POST_GENERATE_XML_FILE_FOR_DOCUMENT, "number");
export const generateXmlDocument = async (documentId: string): Promise<number> => {
  return await _generateXmlDocument.post({ DocumentId: documentId }, null);
};

const _getDocumentContractor = new CancellableHelper<Contractor>(URL_GET_DOCUMENT_CONTRACTOR, Contractor);
export const getDocumentContractor = async (documentId: string): Promise<Contractor> => {
  return await _getDocumentContractor.get({ DocumentId: documentId });
};

const _generateVirtual = new CancellableHelper<void>(URL_POST_GENERATE_VIRTUAL);
export const generateVirtual = async (costDocumentId: string, documentIds: string[]): Promise<void> => {
  return _generateVirtual.post({ costDocumentId: costDocumentId }, documentIds);
};

const _sendToKsef = new CancellableHelper<void>(URL_POST_SEND_TO_KSEF);
export const sendToKsef = async (documentId: string): Promise<void> => {
  return _sendToKsef.post({ DocumentId: documentId }, null);
};

const _isAvailableKsefVisualization = new CancellableHelper<boolean>(URL_GET_IS_AVAILABLE_KSEF_VISUALIZATION);
export const isAvailableKsefVisualization = async (documentId: string): Promise<boolean> => {
  return _isAvailableKsefVisualization.get({ DocumentId: documentId });
};

const _isAvailableKsefOfflineVisualization = new CancellableHelper<DoubleConfirm>(
  URL_GET_IS_AVAILABLE_KSEF_OFFLINE_VISUALIZATION
);
export const isAvailableKsefOfflineVisualization = async (documentId: string): Promise<DoubleConfirm> => {
  return _isAvailableKsefOfflineVisualization.get({ DocumentId: documentId });
};

const _trySetOffline = new CancellableHelper<void>(URL_POST_TRY_SET_KSEF_OFFLINE);
export const trySetOffline = async (documentId: string): Promise<void> => {
  return _trySetOffline.post({ DocumentId: documentId });
};

const _toggleKsefStopExport = new CancellableHelper<void>(URL_POST_TOGGLE_KSEF_STOP);
export const toggleKsefStopExport = async (documentId: string): Promise<void> => {
  return _toggleKsefStopExport.post({ DocumentId: documentId });
};
